<template>
    <app-button
        @click="modalState.isOpen = true"
        class="
            bg-transparent
            text-primary
            border-primary border
            flex
            items-center
            gap-2
        "
    >
        <ion-icon name="add-outline" class="text-xl"></ion-icon>
        Invite Team Members
    </app-button>
    <modal-custom
        :is-open="modalState.isOpen"
        title="Invite Team Members"
        @close="tryCloseModal"
        z-index="1001"
    >
        <div
            v-if="isInvited && !error"
            class="p-4 bg-primary text-white rounded-md my-2 text-sm"
        >
            {{ success }}
        </div>
        <div
            v-if="error"
            class="p-4 bg-red-500 text-white rounded-md my-2 text-sm"
        >
            {{ error.toString() }}
        </div>
        <h3 class="text-sm text-gray-400">Invite your team members here</h3>
        <div
            v-for="(invite, index) in inviteList"
            class="
                grid
                !grid-cols-[1fr_0.5fr_auto_auto]
                items-center
                gap-4
                my-2
            "
        >
            <div>
                <NewUserAutocompleteInput
                    :disabled="isInviting"
                    v-model="invite.email"
                    placeholder="Name or Email"
                    class="w-full"
                />
            </div>
            <div>
                <app-select
                    :disabled="isInviting"
                    type="text"
                    v-model="invite.role"
                    placeholder="Role"
                    class="w-full"
                >
                    <option value="owner">Admin</option>
                    <option value="leader">Leader</option>
                    <option value="member">Member</option>
                    <option value="spectator">Spectator</option>
                </app-select>
            </div>
            <div class="flex items-center gap-2">
                <input
                    type="checkbox"
                    name="is_premium"
                    id="is_premium_checkbox"
                    @input="
                        invite.type = $event.target.checked
                            ? 'admin_paid'
                            : 'self_paid'
                    "
                    :checked="invite.type === 'admin_paid'"
                />
                <label for="is_premium_checkbox">Premium</label>
                <input type="hidden" name="type" :value="invite.type" />
            </div>
            <app-button
                @click="removeInvite(index)"
                class="w-full"
                :disabled="index === 0 || isInviting"
                type="button"
            >
                <ion-icon name="trash"></ion-icon>
            </app-button>
        </div>
        <hr />

        <div
            v-if="isRequiringPayment"
            class="flex items-center my-4 gap-4 w-full"
        >
            <div
                class="
                    p-4
                    text-sm
                    bg-orange-100
                    text-orange-900
                    rounded-md
                    w-full
                "
            >
                You are limited to
                <strong>{{ userLimit }}</strong> complimentary team members.
                Payment information is required if you want to invite more than
                <strong>{{ userLimit }}</strong> users.
                <br />
                <a
                    class="
                        hover:bg-orange-300
                        bg-orange-200
                        py-2
                        px-4
                        font-semibold
                        rounded-md
                        inline-block
                        mt-2
                    "
                    href="/account/billing"
                >
                    Click here to enter payment information
                </a>
            </div>
        </div>
        <div v-else>
            <div
                class="p-4 text-sm bg-blue-100 text-blue-900 rounded-md w-full"
            >
                <h4>About Membership Type</h4>
                <ul>
                    <li>
                        <strong>Premium</strong>: The team owner will pay for
                        this member
                    </li>
                    <li>
                        <strong>Standard</strong>: The invited member will have
                        to pay to use the team
                    </li>
                </ul>
            </div>
        </div>
        <div class="flex items-center my-4 gap-4">
            <div class="mr-auto">
                <a
                    v-if="canAddMore"
                    @click="addInvite"
                    class="flex items-center gap-2 text-primary cursor-pointer"
                >
                    <ion-icon name="add-circle-outline" size="lg"></ion-icon>
                    <div>Add Another</div>
                </a>
            </div>
            <div class="ml-auto flex items-center gap-8">
                <div v-if="estimatedPrice !== 0" class="text-sm text-gray-800">
                    {{ inviteList.length }} x {{ displayedPrice }} = ${{
                        estimatedPrice
                    }}
                </div>
                <app-button
                    :disabled="!isValid() || isInviting"
                    @click="sendInvite"
                    class="
                        ml-auto
                        !bg-primary
                        text-white
                        flex
                        items-center
                        gap-2
                    "
                    type="button"
                >
                    {{ isInviting ? "Sending invitation..." : "Send Invite" }}
                    <simple-loading-circle
                        v-if="isInviting"
                        size="16"
                    ></simple-loading-circle>
                </app-button>
            </div>
        </div>
        <div
            v-if="estimatedPrice !== 0"
            class="mt-8 text-sm bg-accent text-white py-2 px-4 rounded-lg"
        >
            A total of ${{ estimatedPrice }} will be added to your next invoice.
        </div>
    </modal-custom>
</template>

<script setup>
import ModalCustom from "./ModalCustom.vue";
import { computed, onMounted, reactive, ref } from "vue";
import AppButton from "./AppButton.vue";
import AppSelect from "./AppSelect.vue";
import SimpleLoadingCircle from "./SimpleLoadingCircle.vue";
import NewUserAutocompleteInput from "./forms/NewUserAutocompleteInput.vue";

const props = defineProps([
    "api.invite",
    "premiumUserCount",
    "userLimit",
    "isRequiringPayment",
    "userPrice",
]);

const modalState = reactive({
    isOpen: false,
});
const inviteList = reactive([
    {
        email: "",
        role: "member",
        type:
            props.isRequiringPayment || props.premiumUserCount < props.userLimit
                ? "admin_paid"
                : "self_paid",
    },
]);

const isInviting = ref(false);
const isInvited = ref(false);
const error = ref(null);
const success = ref(null);

function addInvite() {
    inviteList.push(
        reactive({
            email: "",
            role: "member",
            type: isPremiumStillAvailable.value ? "admin_paid" : "self_paid",
        })
    );
}

function removeInvite(index) {
    inviteList.splice(index, 1);
}

function sendInvite() {
    const teamIdFromQueryString = new URLSearchParams(
        window.location.search
    ).get("teamId");
    isInviting.value = true;
    isInvited.value = false;
    error.value = null;
    const inviteListRequest = inviteList.map((invite) => {
        const obj = {
            role: invite.role,
        };
        if (typeof invite.email === "string") {
            obj.email = invite.email;
        } else {
            obj.id = invite.email.id;
        }
        obj.type = invite.type;
        return obj;
    });
    const res = axios
        .post(props["api.invite"], {
            invites: inviteListRequest,
            teamId: teamIdFromQueryString,
        })
        .then((res) => {
            isInviting.value = false;
            isInvited.value = true;
            inviteList.splice(1);
            inviteList[0].email = "";
            inviteList[0].role = "-1";
            if (res.data.success) {
                success.value = res.data.message;
                setTimeout(() => {
                    window.location.reload();
                }, 1000);
            } else {
                error.value = res.data.message;
            }
        })
        .catch((err) => {
            isInviting.value = false;
            error.value = err.response.data.message;
        });
}

const isValid = () => {
    const emailRegex = /^[\w-.+]+@([\w-]+\.)+[\w-]{2,4}$/;

    return (
        inviteList.every((invite, index) => {
            return (
                invite.email &&
                (emailRegex.test(invite.email) || invite.email?.id) &&
                !inviteList.find((i, iIndex) => {
                    let emailMatch = false;
                    if (typeof i.email === "string") {
                        emailMatch = i.email === invite.email;
                    } else {
                        emailMatch = i.email?.id === invite.email?.id;
                    }
                    return emailMatch && index !== iIndex;
                }) &&
                invite.role !== "-1"
            );
        }) && inviteList.length > 0
    );
};

const tryCloseModal = () => {
    if (!isInviting.value) {
        modalState.isOpen = false;
        isInvited.value = false;
        inviteList.splice(1);
        inviteList[0].email = "";
        inviteList[0].role = "member";
        error.value = null;
    }
};

const canAddMore = computed(() => {
    const limitReached =
        parseInt(props.premiumUserCount) + inviteList.length >=
        parseInt(props.userLimit);
    return !limitReached || !props.isRequiringPayment;
});

const estimatedPrice = computed(() => {
    const existingPremiumUserCount = parseInt(props.premiumUserCount);
    const newPremiumUserCount = inviteList.filter(
        (invite) => invite.type === "admin_paid"
    ).length;
    const freeLimit = parseInt(props.userLimit) - existingPremiumUserCount;

    if (newPremiumUserCount > freeLimit) {
        const price =
            (newPremiumUserCount - freeLimit) * parseInt(props.userPrice);
        return parseFloat(price / 100).toFixed(2);
    }

    return 0;
});

const isPremiumStillAvailable = computed(() => {
    const existingPremiumUserCount = parseInt(props.premiumUserCount);
    const newPremiumUserCount = inviteList.filter(
        (invite) => invite.type === "admin_paid"
    ).length;
    const freeLimit = parseInt(props.userLimit) - existingPremiumUserCount;

    if (newPremiumUserCount >= freeLimit) {
        return false;
    }

    return true;
});

const displayedPrice = computed(() => {
    return parseFloat(props.userPrice / 100).toFixed(2);
});

onMounted(() => {
    const hashkv = new URLSearchParams(window.location.hash);
    if (hashkv.has("#invite")) {
        modalState.isOpen = true;

        inviteList[0].email = hashkv.get("#invite");
    }
});
</script>

<style scoped></style>
